import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Typography } from '@mui/material';
import { LockClockRounded, ShieldOutlined } from '@mui/icons-material';
import { useUser } from '../../../contexts/UserContext';
import { formatHoldUntil, holdUntil } from '../../../utils/securityHold';
import { PT } from '../../../components/portalUi';

/* The 48 hours after a two-factor reset (utils/securityHold.ts): the server refuses
 * withdrawals, marketplace listings and purchases, wallet changes (in the game too) and
 * physical-card orders until the hold
 * lifts, so the portal says so up front rather than letting each button discover
 * it. Not dismissable — it is the state of the account, not a tip — and it goes
 * away on its own when the time comes.
 *
 * After the hold lifts, a member whose 2FA was reset and never set back up keeps
 * a slimmer reminder until they do (owner's call, 2026-10-24): the reset leaves
 * `securityHoldUntil` on the account for good, so "had a reset, and 2FA is off"
 * is readable from the sign-in answer alone. It says where the responsibility
 * sits — an account that stays on a password alone is the member's choice. */
export const SecurityHoldNotice = () => {
  const { user } = useUser();
  const [now, setNow] = useState(Date.now());
  const until = holdUntil(user, now);
  const untilMs = until ? until.getTime() : null;

  // Re-render at the moment it lifts (or once a minute, whichever is sooner). Keyed on
  // the instant, not the Date object, so a parent's re-render does not re-arm it.
  useEffect(() => {
    if (untilMs === null) return undefined;
    const wait = Math.min(untilMs - now, 60000);
    const t = setTimeout(() => setNow(Date.now()), Math.max(wait, 1000));
    return () => clearTimeout(t);
  }, [untilMs, now]);

  const neverSetBackUp =
    Boolean(user?.securityHoldUntil) && !user?.hasTwoFactorAuthEnabled;
  if (!until && !neverSetBackUp) return null;

  const setUpLink = (
    <Link
      component={RouterLink}
      to="/member-portal/settings?tab=security"
      sx={{
        display: 'inline-block',
        mt: 0.5,
        fontSize: 14,
        fontWeight: 700,
        color: PT.teal,
        textDecorationColor: 'rgba(56,232,255,0.5)'
      }}
    >
      Set up 2FA again in Settings → Security
    </Link>
  );
  const onYou = (
    <Typography
      sx={{ mt: 0.5, fontSize: 13.5, color: PT.textMuted, lineHeight: 1.45 }}
    >
      Until you do, your account is protected by your password alone. INCRYPT
      is not responsible for any loss from access to an account that has not
      set two-factor authentication back up.
    </Typography>
  );

  if (!until) {
    return (
      <Box
        role="status"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          mx: 2,
          mt: 2,
          mb: 1,
          p: { xs: 1.5, sm: 2 },
          borderRadius: '14px',
          border: '1px solid rgba(243,191,77,0.45)',
          background: 'rgba(243,191,77,0.08)'
        }}
      >
        <ShieldOutlined sx={{ color: PT.gold, flexShrink: 0, mt: '1px' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14.5,
              fontWeight: 700,
              color: PT.text,
              lineHeight: 1.45
            }}
          >
            Your two-factor authentication was reset and has not been set up
            again.
          </Typography>
          {onYou}
          {setUpLink}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      role="status"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        mx: 2,
        mt: 2,
        mb: 1,
        p: { xs: 1.5, sm: 2 },
        borderRadius: '14px',
        border: '1px solid rgba(243,191,77,0.45)',
        background: 'rgba(243,191,77,0.08)'
      }}
    >
      <LockClockRounded sx={{ color: PT.gold, flexShrink: 0, mt: '1px' }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14.5, fontWeight: 700, color: PT.text, lineHeight: 1.45 }}>
          Your two-factor authentication was reset. Withdrawals, marketplace listings and purchases,
          wallet changes and physical-card orders are paused until {formatHoldUntil(until)}.
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 13.5, color: PT.textMuted, lineHeight: 1.45 }}>
          Playing, packs, deposits from the wallet already on your account, and password changes are open as
          usual. If you did not ask for this reset, change your password now and contact support.
        </Typography>
        {!user?.hasTwoFactorAuthEnabled && (
          <>
            {onYou}
            {setUpLink}
          </>
        )}
      </Box>
    </Box>
  );
};
